import { setValueByPath } from '@apia/util';
import {
  FC,
  MutableRefObject,
  ReactNode,
  Suspense,
  useMemo,
  useRef,
} from 'react';
import { ThemeUICSSObject, Box, useThemeUI } from 'theme-ui';
import { getVariant } from '../util/getVariant';
import { CustomThemeProvider } from './CustomThemeProvider';

/**
 * Permite crear un componente cuyos estilos pueden ser redefinidos por un
 * usuario final.
 *
 * Esto es así puesto que los estilos definidos serán agregados a un árbol que
 * el usuario puede consultar y sobreescribir. De esta manera los estilos
 * finales del componente serán aquellos definidos en este método pero
 * sobreescritos por los definidos por el usuario si es que existen.
 *
 * **Importante** Para aplicar la variante, se aplica un box alrededor del
 * componente que se está renderizando, con className="variant__holder". En caso
 * de que se quisiera evitar este comportamiento, se debe pasar unwraped = true
 *
 * @param displayName Este parámetro es importante para el debugger de React.
 * @param stylesPath La ruta donde se aplicarán los estilos, es importante asegurarse de que esa ruta ya no está siendo utilizada por otro componente.
 * @param styles Un objeto de estilos de ThemeUI
 * @param Component La definición del componente.
 * @returns Un componente reutilizable y exportable que tiene estilos aplicados.
 */
export function makeStyledComponent<T extends object>(
  displayName: string,
  stylesPath: string,
  styles: ThemeUICSSObject,
  Component: FC<T>,
  unwraped = false,
) {
  return Object.assign(
    (props: T) => {
      const avoidFirstRender = useRef(0);
      const { theme: tuiTheme } = useThemeUI();
      const theme = useMemo(
        () => () => ({
          ...tuiTheme,
          ...setValueByPath({}, stylesPath, styles),
        }),
        [tuiTheme],
      );

      if (unwraped) {
        return (
          <CustomThemeProvider customTheme={theme}>
            <NoThemePrevent avoidFirstRender={avoidFirstRender}>
              <Suspense>
                <Component {...props} />
              </Suspense>
            </NoThemePrevent>
          </CustomThemeProvider>
        );
      }
      return (
        <CustomThemeProvider customTheme={theme}>
          <NoThemePrevent avoidFirstRender={avoidFirstRender}>
            <Box className="variant__holder" {...getVariant(stylesPath)}>
              <Suspense>
                <Component {...props} />
              </Suspense>
            </Box>
          </NoThemePrevent>
        </CustomThemeProvider>
      );
    },
    { displayName },
  );
}

const NoThemePrevent = ({
  avoidFirstRender,
  children,
}: {
  avoidFirstRender: MutableRefObject<number>;
  children: ReactNode;
}) => {
  // eslint-disable-next-line no-param-reassign
  avoidFirstRender.current++;

  if (avoidFirstRender.current <= 1) return null;

  return <>{children}</>;
};
